import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './AuthModal.css';

const AuthModal = ({ isOpen, onClose, onLogin, onRegister }) => {
  const { t } = useTranslation();
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '', // 保留密码字段用于登录模式
    email: '',
    shopName: '',
    userPhone: '',
    shopAddress: '',
    city: '',
    country: '',
    countryCode: '',
    phoneAreaCode: '',
    recommender: ''
  });
  const [errors, setErrors] = useState({});
  const [privacyAgreed, setPrivacyAgreed] = useState(false);

  if (!isOpen) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = t('auth.usernameRequired', '用户名是必填项');
    } else if (formData.username.length < 3) {
      newErrors.username = t('auth.usernameMinLength', '用户名至少需要3个字符');
    }

    // 登录模式下才验证密码
    if (isLoginMode) {
      if (!formData.password) {
        newErrors.password = t('auth.passwordRequired', '密码是必填项');
      } else if (formData.password.length < 6) {
        newErrors.password = t('auth.passwordMinLength', '密码至少需要6个字符');
      }
    }

    if (!isLoginMode) {
      if (!formData.email.trim()) {
        newErrors.email = t('auth.emailRequired', '邮箱是必填项');
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = t('auth.emailInvalid', '请输入有效的邮箱地址');
      }

      if (!formData.shopName.trim()) {
        newErrors.shopName = t('auth.shopNameRequired', '店铺名称是必填项');
      }

      if (!formData.userPhone.trim()) {
        newErrors.userPhone = t('auth.phoneRequired', '手机号是必填项');
      }

      if (!formData.shopAddress.trim()) {
        newErrors.shopAddress = t('auth.shopAddressRequired', '店铺地址是必填项');
      }

      if (!privacyAgreed) {
        newErrors.privacyAgreed = t('auth.privacyAgreementRequired', '您必须同意隐私政策才能继续');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (isLoginMode) {
      // Call login handler
      if (onLogin) {
        onLogin({
          username: formData.username,
          password: formData.password
        });
      }
    } else {
      // Call register handler
      if (onRegister) {
        onRegister({
          username: formData.username,
          email: formData.email,
          shopName: formData.shopName,
          userPhone: formData.userPhone,
          shopAddress: formData.shopAddress,
          city: formData.city,
          country: formData.country,
          countryCode: formData.countryCode,
          phoneAreaCode: parseInt(formData.phoneAreaCode) || 0,
          recommender: formData.recommender
        });
      }
    }

    // Reset form
    setFormData({
      username: '',
      password: '', // 保留密码字段用于登录模式
      email: '',
      shopName: '',
      userPhone: '',
      shopAddress: '',
      city: '',
      country: '',
      countryCode: '',
      phoneAreaCode: '',
      recommender: ''
    });
    setErrors({});
  };

  const handleSwitchMode = () => {
    setIsLoginMode(!isLoginMode);
    setErrors({});
    setPrivacyAgreed(false);
    setFormData({
      username: '',
      password: '', // 保留密码字段用于登录模式
      email: '',
      shopName: '',
      userPhone: '',
      shopAddress: '',
      city: '',
      country: '',
      countryCode: '',
      phoneAreaCode: '',
      recommender: ''
    });
  };

  const handleClose = () => {
    setErrors({});
    setPrivacyAgreed(false);
    setFormData({
      username: '',
      password: '', // 保留密码字段用于登录模式
      email: '',
      shopName: '',
      userPhone: '',
      shopAddress: '',
      city: '',
      country: '',
      countryCode: '',
      phoneAreaCode: '',
      recommender: ''
    });
    onClose();
  }

  return (
    <div className="auth-modal-overlay" onClick={handleClose}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <button className="auth-modal-close" onClick={handleClose}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>

        <div className="auth-modal-header">
          <h2>{isLoginMode ? t('auth.login', '登录') : t('auth.signUp', '注册')}</h2>
          <p className="auth-modal-subtitle">
            {isLoginMode 
              ? t('auth.loginSubtitle', '欢迎回来！请登录您的账户。') 
              : t('auth.signUpSubtitle', '创建新账户以开始使用。')}
          </p>
        </div>

        <form className="auth-modal-form" onSubmit={handleSubmit}>
          <div className="auth-form-group">
            <label htmlFor="username">{t('auth.username', '用户名')}</label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              placeholder={t('auth.usernamePlaceholder', '请输入您的用户名')}
              className={errors.username ? 'error' : ''}
            />
            {errors.username && <span className="error-message">{errors.username}</span>}
          </div>

          {!isLoginMode && (
            <div className="auth-form-group">
              <label htmlFor="email">{t('auth.email', '邮箱')}</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder={t('auth.emailPlaceholder', '请输入您的邮箱')}
                className={errors.email ? 'error' : ''}
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>
          )}

          {!isLoginMode && (
            <div className="auth-form-group">
              <label htmlFor="shopName">{t('auth.shopName', '店铺名称')}</label>
              <input
                type="text"
                id="shopName"
                name="shopName"
                value={formData.shopName}
                onChange={handleInputChange}
                placeholder={t('auth.shopNamePlaceholder', '请输入您的店铺名称')}
                className={errors.shopName ? 'error' : ''}
              />
              {errors.shopName && <span className="error-message">{errors.shopName}</span>}
            </div>
          )}

          {!isLoginMode && (
            <div className="auth-form-group">
              <label htmlFor="userPhone">{t('auth.phone', '手机号')}</label>
              <input
                type="tel"
                id="userPhone"
                name="userPhone"
                value={formData.userPhone}
                onChange={handleInputChange}
                placeholder={t('auth.phonePlaceholder', '请输入您的手机号')}
                className={errors.userPhone ? 'error' : ''}
              />
              {errors.userPhone && <span className="error-message">{errors.userPhone}</span>}
            </div>
          )}

          {!isLoginMode && (
            <div className="auth-form-group">
              <label htmlFor="shopAddress">{t('auth.shopAddress', '店铺地址')}</label>
              <input
                type="text"
                id="shopAddress"
                name="shopAddress"
                value={formData.shopAddress}
                onChange={handleInputChange}
                placeholder={t('auth.shopAddressPlaceholder', '请输入您的店铺地址')}
                className={errors.shopAddress ? 'error' : ''}
              />
              {errors.shopAddress && <span className="error-message">{errors.shopAddress}</span>}
            </div>
          )}

          {!isLoginMode && (
            <div className="auth-form-group">
              <label htmlFor="city">{t('auth.city', '城市')}</label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                placeholder={t('auth.cityPlaceholder', '请输入城市')}
                className={errors.city ? 'error' : ''}
              />
            </div>
          )}

          {!isLoginMode && (
            <div className="auth-form-group">
              <label htmlFor="country">{t('auth.country', '国家')}</label>
              <input
                type="text"
                id="country"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                placeholder={t('auth.countryPlaceholder', '请输入国家')}
                className={errors.country ? 'error' : ''}
              />
            </div>
          )}

          {!isLoginMode && (
            <div className="auth-form-group">
              <label htmlFor="countryCode">{t('auth.countryCode', '国家编码')}</label>
              <input
                type="text"
                id="countryCode"
                name="countryCode"
                value={formData.countryCode}
                onChange={handleInputChange}
                placeholder={t('auth.countryCodePlaceholder', '请输入国家编码')}
                className={errors.countryCode ? 'error' : ''}
              />
            </div>
          )}

          {!isLoginMode && (
            <div className="auth-form-group">
              <label htmlFor="phoneAreaCode">{t('auth.phoneAreaCode', '手机区号')}</label>
              <input
                type="number"
                id="phoneAreaCode"
                name="phoneAreaCode"
                value={formData.phoneAreaCode}
                onChange={handleInputChange}
                placeholder={t('auth.phoneAreaCodePlaceholder', '请输入手机区号')}
                className={errors.phoneAreaCode ? 'error' : ''}
              />
            </div>
          )}

          {!isLoginMode && (
            <div className="auth-form-group">
              <label htmlFor="recommender">{t('auth.recommender', '推荐人')}</label>
              <input
                type="text"
                id="recommender"
                name="recommender"
                value={formData.recommender}
                onChange={handleInputChange}
                placeholder={t('auth.recommenderPlaceholder', '请输入推荐人（可选）')}
                className={errors.recommender ? 'error' : ''}
              />
            </div>
          )}

          {/* 仅在登录模式显示密码字段 */}
          {isLoginMode && (
            <div className="auth-form-group">
              <label htmlFor="password">{t('auth.password', '密码')}</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder={t('auth.passwordPlaceholder', '请输入您的密码')}
                className={errors.password ? 'error' : ''}
              />
              {errors.password && <span className="error-message">{errors.password}</span>}
            </div>
          )}

          {!isLoginMode && (
            <div className="auth-privacy-agreement">
              <label className="auth-checkbox-label">
                <input
                  type="checkbox"
                  className="auth-checkbox"
                  checked={privacyAgreed}
                  onChange={(e) => {
                    setPrivacyAgreed(e.target.checked);
                    if (errors.privacyAgreed) {
                      setErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.privacyAgreed;
                        return newErrors;
                      });
                    }
                  }}
                />
                <span>
                  {t('auth.privacyAgreement', '我已阅读并同意')}
                  <button
                    type="button"
                    className="auth-privacy-link"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowPrivacyPolicy(true);
                    }}
                  >
                    {t('auth.privacyPolicy', '隐私政策')}
                  </button>
                </span>
              </label>
              {errors.privacyAgreed && <span className="error-message">{errors.privacyAgreed}</span>}
            </div>
          )}

          <button type="submit" className="auth-submit-btn">
            {isLoginMode ? t('auth.login', '登录') : t('auth.signUp', '注册')}
          </button>
        </form>

        <div className="auth-modal-footer">
          <p>
            {isLoginMode ? t('auth.noAccount', '还没有账户？') : t('auth.haveAccount', '已有账户？')}
            <button 
              type="button" 
              className="auth-switch-btn"
              onClick={handleSwitchMode}
            >
              {isLoginMode ? t('auth.signUp', '注册') : t('auth.login', '登录')}
            </button>
          </p>
        </div>
      </div>

      {/* Privacy Policy Modal */}
      {showPrivacyPolicy && (
        <div className="privacy-modal-overlay" onClick={() => setShowPrivacyPolicy(false)}>
          <div className="privacy-modal" onClick={(e) => e.stopPropagation()}>
            <div className="privacy-modal-header">
              <h3>{t('auth.privacyPolicy', '隐私政策')}</h3>
              <button 
                className="privacy-modal-close" 
                onClick={() => setShowPrivacyPolicy(false)}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="privacy-modal-content">
              <iframe 
                src="/privacy-policy.html" 
                title={t('auth.privacyPolicy', '隐私政策')}
                className="privacy-iframe"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuthModal;

